// src/providers/user-store-provider.tsx
"use client";

import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useStore } from "zustand";

import {
  type UserStore,
  createUserStore,
  type UserState,
} from "@/stores/user-store"; // Ensure UserState is exported
import { createClient } from "@/lib/supabase/client";

export type UserStoreApi = ReturnType<typeof createUserStore>;

export const UserStoreContext = createContext<UserStoreApi | undefined>(
  undefined
);

// Define a default state for when no user is logged in or profile is not found
const defaultUserState = {
  id: "",
  email: "",
  name: undefined,
  avatar: undefined,
  user_type: null as "admin" | "expert" | "user" | null,
  type: undefined,
  created_at: new Date(0),
  updated_at: undefined,
};

export const UserStoreProvider = ({ children }: { children: ReactNode }) => {
  const storeRef = useRef<UserStoreApi | null>(null);
  const [isStoreInitialized, setIsStoreInitialized] = useState(false);

  // Create Supabase client instance
  // This is already a singleton pattern, so we can reuse it
  // across the application without creating multiple instances.
  // https://supabase.com/docs/guides/auth/server-side/nextjs
  const supabaseClient = createClient();

  useEffect(() => {
    let mounted = true;

    const initializeStore = async () => {
      let initialDataForStore: UserState = { user: defaultUserState };
      try {
        const {
          data: { session },
          error: sessionError,
        } = await supabaseClient.auth.getSession();
        if (sessionError) {
          console.error("Error getting initial session:", sessionError.message);
          // Early return on session error
          return;
        }
        if (!session?.user) {
          initialDataForStore = { user: defaultUserState };
          return;
        }
        // Only runs if session.user exists
        const profile = await fetchPublicDataOfUser(session.user.id);
        if (profile && profile.user_type === "admin") {
          initialDataForStore = {
            user: {
              id: profile.id,
              email: session.user.email || "",
              name: profile.name || undefined,
              avatar: profile.avatar || undefined,
              user_type: profile.user_type as
                | "admin"
                | "expert"
                | "user"
                | null,
              type: profile.type || undefined,
              created_at: new Date(profile.created_at),
              updated_at: profile.updated_at
                ? new Date(profile.updated_at)
                : undefined,
            },
          };
        } else {
          // If user is not admin or profile not found, use default state and sign out
          if (session.user) {
            console.warn("Non-admin user detected, signing out...");
            await supabaseClient.auth.signOut();
          }
          initialDataForStore = { user: defaultUserState };
        }
      } catch (e) {
        console.error("Unexpected error during store initialization:", e);
      } finally {
        if (mounted) {
          if (!storeRef.current) {
            storeRef.current = createUserStore(initialDataForStore);
          } else {
            storeRef.current.getState().updateUser(initialDataForStore.user);
          }
          setIsStoreInitialized(true);
        }
      }
    };

    initializeStore();

    const { data: authListener } = supabaseClient.auth.onAuthStateChange(
      (event, session) => {
        setTimeout(async () => {
          if (!storeRef.current) {
            console.warn("onAuthStateChange: storeRef not initialized yet.");
            return;
          }
          if (event === "SIGNED_IN" && session?.user) {
            // Check if user is admin before updating store
            const profile = await fetchPublicDataOfUser(session.user.id);
            if (profile && profile.user_type === "admin") {
              await fetchProfileAndUpdateStore(
                storeRef.current,
                session.user.id,
                session.user.email
              );
            } else {
              // Non-admin user, sign them out
              console.warn(
                "Non-admin user attempted to sign in, signing out..."
              );
              await supabaseClient.auth.signOut();
            }
          } else if (event === "SIGNED_OUT") {
            storeRef.current.getState().updateUser(defaultUserState);
          } else if (event === "USER_UPDATED" && session?.user) {
            // Check if user is still admin before updating store
            const profile = await fetchPublicDataOfUser(session.user.id);
            if (profile && profile.user_type === "admin") {
              await fetchProfileAndUpdateStore(
                storeRef.current,
                session.user.id,
                session.user.email
              );
            } else {
              // User is no longer admin, sign them out
              await supabaseClient.auth.signOut();
            }
          }
        }, 0);
      }
    );

    return () => {
      authListener.subscription.unsubscribe();
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Render children only after the store is initialized
  if (!isStoreInitialized || !storeRef.current) {
    return null; // Or a loading spinner, or some fallback UI
  }

  return (
    <UserStoreContext.Provider value={storeRef.current}>
      {children}
    </UserStoreContext.Provider>
  );
};

export const useUserStore = <T,>(selector: (store: UserStore) => T): T => {
  const userStoreContext = useContext(UserStoreContext);

  if (!userStoreContext) {
    // This error implies that useUserStore is used outside UserStoreProvider
    // or before the store is initialized and provider has rendered.
    throw new Error(
      `useUserStore must be used within a UserStoreProvider, and after the store has been initialized.`
    );
  }

  return useStore(userStoreContext, selector);
};

// --- Helper Functions ---

/**
 * Fetches the public user data from Supabase for a given user ID.
 *
 * - Queries the `user` table.
 * - Returns the user data object or null if not found.
 * - Throws an error for any Supabase error except for code "PGRST116" (row not found).
 *
 * @param {string} userId - The ID of the user to fetch.
 * @returns {Promise<any | null>} The user data object or null if not found.
 * @throws {Error} If a Supabase error occurs (other than not found).
 *
 * @example
 * const user = await fetchPublicDataOfUser("123");
 * if (user) {
 *   console.log(user.full_name);
 * }
 */
async function fetchPublicDataOfUser(userId: string) {
  const supabaseClient = createClient();
  const { data, error } = await supabaseClient
    .from("user")
    .select("*")
    .eq("id", userId)
    .maybeSingle();
  if (error && error.code !== "PGRST116") {
    throw error;
  }
  return data || null;
}

/**
 * Updates the zustand user store with the given user profile data.
 * If no profile is found, falls back to minimal info.
 */

// Import the actual database types
import { Tables } from "@/lib/supabase/database.types";

// Type for user profile matching database schema
type UserProfile = Tables<"user">;

function updateUserStoreWithProfile(
  store: UserStoreApi,
  profile: UserProfile | null,
  userId: string,
  userEmail?: string
) {
  // Only update store if user is admin
  if (profile && profile.user_type === "admin") {
    store.getState().updateUser({
      id: profile.id,
      email: userEmail || "",
      name: profile.name || undefined,
      avatar: profile.avatar || undefined,
      user_type: profile.user_type as "admin" | "expert" | "user" | null,
      type: profile.type || undefined,
      created_at: new Date(profile.created_at),
      updated_at: profile.updated_at ? new Date(profile.updated_at) : undefined,
    });
  } else {
    // Non-admin or no profile, use default state
    store.getState().updateUser({
      ...defaultUserState,
      id: "",
      email: "",
      user_type: null,
    });
  }
}

/**
 * Fetches the user profile and updates the zustand store.
 * Handles errors and fallback logic.
 */
async function fetchProfileAndUpdateStore(
  store: UserStoreApi,
  userId: string,
  userEmail?: string
) {
  try {
    const profile = await fetchPublicDataOfUser(userId);
    updateUserStoreWithProfile(store, profile, userId, userEmail);
  } catch (e) {
    console.error("Unexpected error in fetchProfileAndUpdateStore:", e);
    updateUserStoreWithProfile(store, null, userId, userEmail);
  }
}
